<template>
  <div class="mb-5">
    <div
      class="col-12 d-flex flex-wrap filter-row"
    >
      <div>
        <base-input :label="$t('COMMON.PER_PAGE')">
          <el-select
            class="select-primary pagination-select"
            v-model="pagination.perPage"
            :placeholder="$t('COMMON.PER_PAGE')"
          >
            <el-option
              class="select-primary"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>
        </base-input>
      </div>
      <div>
        <base-input :label="$t('COMMON.SUPPLIERS')">
          <supplier-selector
            :multiple="false"
            :allowNone="false"
            :showAll="true"
            :placeholder="$t('COMMON.SUPPLIERS')"
            @supplierChanged="(supplier) => (selectedSupplier = supplier)"
          />
        </base-input>
      </div>
      <div>
        <base-input :label="$t('COMMON.CATEGORIES')">
          <category-group-selector
            :allowNone="false"
            :showAll="true"
            :isOnlyModels="false"
            :placeholder="$t('COMMON.CATEGORIES')"
            @categoryChanged="
              (categoryId) => {
                selectedCategory = categoryId;
              }
            "
          />
        </base-input>
      </div>
      <div>
        <base-input
          v-model="query"
          type="search"
          prepend-icon="fas fa-search"
          :placeholder="$t('COMMON.SEARCH_HINT.PRODUCT_WITH_SUPPLIER_CODE')"
          :label="$t('COMMON.SEARCH')"
          clearable
        />
      </div>
      <div v-if="salesOrder.status == ORDER_STATUS_DRAFT">
        <base-checkbox v-model="displayZeroQuantities">
          <span class="form-control-label">
            {{ $t("COMMON.DISPLAY_ZERO_LINES") }}
          </span>
        </base-checkbox>
      </div>
    </div>

    <el-table
      class="table-responsive align-items-center table-flush"
      header-row-class-name="thead-light"
      ref="salesOrderItemsTable"
      :row-class-name="getProductRowClassName"
      :data="filteredProducts.slice(startIndex, endIndex)"
    >
      <div slot="empty" v-if="loading">
        <img src="/img/loading.gif" style="height: 100px; width: 100px" />
      </div>
      <el-table-column :label="$t('COMMON.CODE')" prop="code" min-width="200">
        <template v-slot="{ row }">
          <div v-if="row">
            <div style="display: flex; align-items: center; gap: 8px">
              <a href="#" @click.prevent="goToSalesOrderable(row)">
                {{ row.name }} {{ row.id }}
              </a>
              <i
                v-if="row.status === STATUS_BACKORDER"
                class="fas fa-exclamation-triangle text-warning"
                style="cursor: pointer; font-size: 16px"
                :title="$t('PRODUCTS.PRODUCT_IN_BACKORDER')"
                @click="showBackorderModal(row)"
              ></i>
              <i
                v-if="row.status === STATUS_DELETED"
                class="fas fa-ban text-danger"
                style="cursor: pointer; font-size: 16px"
                :title="$t('PRODUCTS.PRODUCT_DELETED')"
                @click="showDeletedProductModal(row)"
              ></i>
            </div>
            <br />
            <div class="text-muted salesOrder-item-excerpt">
              <ul>
                <li>
                  {{ $t("PRODUCTS.CODE") }} :
                  {{ row.code }}
                </li>
                <li>
                  {{ $t("PRODUCTS.SKU") }} :
                  {{ row.sku }}
                </li>
              </ul>
            </div>
            <!-- <div
              v-if="row.status === STATUS_BACKORDER"
              class="alert alert-warning mb-0 mt-2 py-2 px-3"
              style="font-size: 13px"
            >
              <div class="d-flex align-items-start">
                <i class="fas fa-exclamation-circle mr-2 mt-1"></i>
                <div>
                  <div class="font-weight-bold">
                    {{ $t("PRODUCTS.PRODUCT_IN_BACKORDER") }}
                  </div>
                  <div v-if="row.backorder_note">{{ row.backorder_note }}</div>
                  <div v-else class="text-muted">
                    {{ $t("PRODUCTS.BACKORDER_INFO_MESSAGE") }}
                  </div>
                </div>
              </div>
            </div> -->
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.QUANTITY')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          <base-input
            :disabled="salesOrder.status != ORDER_STATUS_DRAFT"
            v-model="itemsQuantities[row.id]"
            type="number"
            placeholder="0"
            @change="
              (event) => {
                updateSalesOrderItem(row, event.target.value);
              }
            "
          />
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.FORMAT')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          <object-link :object="row.orderUnitOfMeasureUnit" />
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.UNIT_PRICE')"
        prop="orderUnitOfMeasureUnitPrice"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          {{ $formatCurrency(getProductUnitPrice(row)) }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.SUBTOTAL')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          {{
            $formatCurrency(
              itemsByProduct[row.id]
                ? itemsByProduct[row.id].pricing.subtotal
                : 0
            )
          }}
          <!--
            Container deposit shown as a secondary figure rather than its own
            table row: this table is keyed by product, so an extra row would need
            a phantom product. On the printed quote/invoice the deposit does get
            its own line.
          -->
          <div
            v-if="getProductDepositAmount(row) > 0"
            class="text-muted small"
          >
            + {{ $t("COMMON.DEPOSIT") }}
            {{ $formatCurrency(getProductDepositAmount(row)) }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.TAXES')"
        class-name="text-center"
        label-class-name="text-center"
      >
        <template v-slot="{ row }">
          <tax-groups-selector
            v-if="canEditTaxGroups && itemsByProduct[row.id]"
            :filterOrganization="salesOrder.organization.id"
            :taxGroups="getItemTaxGroups(row)"
            @taxGroupsChanged="
              (taxGroups) => updateSalesOrderItemTaxGroups(row, taxGroups)
            "
          />
          <template v-else>
            <span
              v-for="tax in getItemTaxLabels(row)"
              :key="tax"
              class="badge badge-secondary mr-1"
            >
              {{ tax }}
            </span>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <!-- Discounts Section -->
    <div
      v-if="salesOrder.discounts && salesOrder.discounts.length > 0"
      class="mt-3 mb-3"
    >
      <h5>{{ $t("SALES_ORDERS.DISCOUNTS") }}</h5>
      <div class="discount-list">
        <div
          v-for="(discount, index) in salesOrder.discounts"
          :key="index"
          class="discount-item mb-2"
        >
          <div class="card card-stats">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div class="flex-grow-1">
                  <h6 class="card-title mb-1">{{ discount.name }}</h6>
                  <p class="card-text mb-0">
                    <span v-if="discount.type === 'PERCENTAGE'">
                      {{ discount.value }}% -
                      {{
                        $formatCurrency(
                          calculateDiscountAmountFromDiscount(discount)
                        )
                      }}
                    </span>
                    <span v-else>
                      {{ $formatCurrency(discount.value) }}
                    </span>
                  </p>
                </div>
                <div
                  v-if="salesOrder.status == ORDER_STATUS_DRAFT"
                  class="discount-actions"
                >
                  <base-button
                    type="info"
                    size="sm"
                    @click="editDiscount(index)"
                    icon
                  >
                    <i class="fas fa-edit"></i>
                  </base-button>
                  <base-button
                    type="danger"
                    size="sm"
                    @click="deleteDiscount(index)"
                    icon
                  >
                    <i class="fas fa-trash"></i>
                  </base-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="salesOrder.status == ORDER_STATUS_DRAFT" class="my-3">
      <base-button type="primary" size="sm" @click="openDiscountModal" icon>
        <span class="btn-inner--icon">
          <i class="fas fa-PERCENTAGE"></i>
        </span>
        <span class="btn-inner--text">
          {{ $t("SALES_ORDERS.ADD_DISCOUNT") }}
        </span>
      </base-button>
    </div>

    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>

    <!-- Discount Modal -->
    <modal :show="showDiscountModal" modal-classes="modal-secondary" size="lg">
      <template slot="header">
        <h3 class="modal-title">
          {{
            editingDiscountIndex !== null
              ? $t("SALES_ORDERS.EDIT_DISCOUNT")
              : $t("SALES_ORDERS.ADD_DISCOUNT")
          }}
        </h3>
      </template>

      <div class="modal-body">
        <base-input
          :label="`${$t('COMMON.DESCRIPTION')} (*)`"
          :placeholder="$t('COMMON.DESCRIPTION')"
          v-model="discountForm.excerpt"
          input-classes="form-control-alternative"
        />
        <validation-error :errors="discountFormErrors.excerpt" />

        <base-input
          :label="`${$t('SALES_ORDERS.DISCOUNT_TYPE')} (*)`"
          input-classes="form-control-alternative"
        >
          <el-select
            v-model="discountForm.discountType"
            :placeholder="$t('SALES_ORDERS.DISCOUNT_TYPE')"
            @change="onDiscountTypeChange"
          >
            <el-option
              value="PERCENTAGE"
              :label="$t('SALES_ORDERS.PERCENTAGE')"
            />
            <el-option
              value="AMOUNT"
              :label="$t('SALES_ORDERS.FIXED_AMOUNT')"
            />
          </el-select>
        </base-input>
        <validation-error :errors="discountFormErrors.discountType" />

        <base-input
          v-if="discountForm.discountType === 'PERCENTAGE'"
          :label="`${$t('SALES_ORDERS.DISCOUNT_PERCENTAGE')} (*)`"
          :placeholder="$t('SALES_ORDERS.DISCOUNT_PERCENTAGE')"
          v-model="discountForm.discountValue"
          type="number"
          step="0.01"
          min="0"
          max="100"
          input-classes="form-control-alternative"
        />
        <base-input
          v-if="discountForm.discountType === 'AMOUNT'"
          :label="`${$t('SALES_ORDERS.DISCOUNT_AMOUNT')} (*)`"
          :placeholder="$t('SALES_ORDERS.DISCOUNT_AMOUNT')"
          v-model="discountForm.discountValue"
          type="number"
          step="0.01"
          min="0"
          input-classes="form-control-alternative"
        />
        <validation-error :errors="discountFormErrors.discountValue" />

        <!-- <template v-if="!salesOrder.has_no_taxes">
          <base-input
            :label="$t('COMMON.TAX_GROUPS')"
            :placeholder="$t('COMMON.TAX_GROUPS')"
            input-classes="form-control-alternative"
          >
            <tax-groups-selector
              :filterOrganization="salesOrder.organization.id"
              :taxGroups="discountForm.taxGroups.map((tg) => tg.id)"
              @taxGroupsChanged="
                (taxGroupIds) => {
                  updateTaxGroups(taxGroupIds);
                }
              "
            />
          </base-input>
          <validation-error :errors="discountFormErrors.taxGroups" />
        </template> -->

        <div
          v-if="discountForm.discountType && discountForm.discountValue > 0"
          class="mt-3"
        >
          <div class="alert alert-info">
            <strong>{{ $t("SALES_ORDERS.PREVIEW") }}:</strong><br />
            <span v-if="discountForm.discountType === 'PERCENTAGE'">
              {{
                $t("SALES_ORDERS.DISCOUNT_PERCENTAGE_PREVIEW", {
                  PERCENTAGE: discountForm.discountValue,
                  amount: $formatCurrency(calculateDiscountAmount()),
                })
              }}
            </span>
            <span v-else>
              {{
                $t("SALES_ORDERS.DISCOUNT_AMOUNT_PREVIEW", {
                  amount: $formatCurrency(discountForm.discountValue),
                })
              }}
            </span>
          </div>
        </div>
      </div>

      <template slot="footer">
        <base-button type="link" @click="closeDiscountModal">
          {{ $t("COMMON.CANCEL") }}
        </base-button>
        <base-button
          type="primary"
          @click="submitDiscount"
          :disabled="!isDiscountFormValid"
        >
          {{
            editingDiscountIndex !== null
              ? $t("COMMON.UPDATE")
              : $t("COMMON.ADD")
          }}
        </base-button>
      </template>
    </modal>

    <!-- Backorder Modal -->
    <modal
      :show.sync="backorderModalOpened"
      modal-classes="modal-secondary"
      size="lg"
    >
      <div>
        <div class="d-flex align-items-center mb-3">
          <i
            class="fas fa-exclamation-triangle text-warning mr-2"
            style="font-size: 24px"
          ></i>
          <h2 class="mb-0">
            {{ $t("PRODUCTS.PRODUCT_IN_BACKORDER") }}
          </h2>
        </div>

        <div v-if="selectedBackorderProduct">
          <div class="mb-3">
            <strong>{{ $t("COMMON.PRODUCT") }}:</strong>
            {{ selectedBackorderProduct.name }}
          </div>

          <div
            class="mb-3"
            v-if="selectedBackorderProduct.return_to_stock_date"
          >
            <strong>{{ $t("PRODUCTS.RETURN_TO_STOCK_DATE") }}:</strong>
            {{
              $timeZoneDateFormat(
                selectedBackorderProduct.return_to_stock_date,
                "LL"
              )
            }}
            <br />
            <small class="text-muted">
              {{
                $t("PRODUCTS.PRODUCT_BACK_IN_STOCK_ON_X", {
                  date: $timeZoneDateFormat(
                    selectedBackorderProduct.return_to_stock_date,
                    "LL"
                  ),
                })
              }}
            </small>
          </div>

          <div
            v-if="
              selectedBackorderProduct.replacements &&
              selectedBackorderProduct.replacements.length > 0
            "
          >
            <strong>{{ $t("PRODUCTS.REPLACEMENTS") }}:</strong>
            <div class="mt-2">
              <div
                v-for="replacement in selectedBackorderProduct.replacements"
                :key="replacement.id"
                class="replacement-line d-flex align-items-center justify-content-between mb-2"
              >
                <div class="d-flex flex-column">
                  <object-link :object="replacement" />
                  <div class="replacement-meta">
                    {{ $t("PRODUCTS.SKU") }} :
                    <span class="text-body">
                      {{ replacement.sku || "-" }}
                    </span>
                  </div>
                </div>
                <div class="replacement-actions d-flex align-items-center">
                  <el-tooltip :content="$t('COMMON.COPY')" placement="top">
                    <i
                      class="fa fa-copy action-icon mr-3"
                      @click="copyReplacementCode(replacement.sku)"
                    ></i>
                  </el-tooltip>
                  <a
                    href="#"
                    class="replacement-link d-flex align-items-center"
                    @click.prevent="scrollToReplacement(replacement.id)"
                  >
                    <i class="fa fa-link mr-1"></i>
                    {{ $t("PRODUCTS.GO_TO_REPLACEMENT_LINE") }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3" v-if="selectedBackorderProduct.backorder_note">
            <strong>{{ $t("PRODUCTS.BACKORDER_NOTE") }}:</strong>
            <div class="mt-1">{{ selectedBackorderProduct.backorder_note }}</div>
          </div>

          <div class="alert alert-warning mt-3">
            <i class="fas fa-info-circle mr-2"></i>
            {{ $t("PRODUCTS.BACKORDER_QUANTITY_CHANGE_BLOCKED") }}
          </div>
        </div>

        <div class="mt-4 text-right">
          <base-button type="secondary" @click="backorderModalOpened = false">
            {{ $t("COMMON.CLOSE") }}
          </base-button>
        </div>
      </div>
    </modal>

    <modal
      :show.sync="deletedProductModalOpened"
      modal-classes="modal-secondary"
      size="lg"
    >
      <div>
        <div class="d-flex align-items-center mb-3">
          <i class="fas fa-ban text-danger mr-2" style="font-size: 24px"></i>
          <h2 class="mb-0">
            {{ $t("PRODUCTS.PRODUCT_DELETED") }}
          </h2>
        </div>

        <div v-if="selectedDeletedProduct">
          <div class="mb-3">
            <strong>{{ $t("COMMON.PRODUCT") }}:</strong>
            {{ selectedDeletedProduct.name }}
          </div>

          <p class="text-muted">
            {{ $t("PRODUCTS.DELETED_PRODUCT_REPLACEMENTS_MESSAGE") }}
          </p>

          <div
            v-if="
              selectedDeletedProduct.replacements &&
              selectedDeletedProduct.replacements.length > 0
            "
          >
            <strong>{{ $t("PRODUCTS.REPLACEMENTS") }}:</strong>
            <div class="mt-2">
              <div
                v-for="replacement in selectedDeletedProduct.replacements"
                :key="replacement.id"
                class="replacement-line d-flex align-items-center justify-content-between mb-2"
              >
                <div class="d-flex flex-column">
                  <object-link :object="replacement" />
                  <div class="replacement-meta">
                    {{ $t("PRODUCTS.SKU") }} :
                    <span class="text-body">
                      {{ replacement.sku || "-" }}
                    </span>
                  </div>
                </div>
                <div class="replacement-actions d-flex align-items-center">
                  <el-tooltip :content="$t('COMMON.COPY')" placement="top">
                    <i
                      class="fa fa-copy action-icon mr-3"
                      @click="copyReplacementCode(replacement.sku)"
                    ></i>
                  </el-tooltip>
                  <a
                    href="#"
                    class="replacement-link d-flex align-items-center"
                    @click.prevent="scrollToReplacement(replacement.id)"
                  >
                    <i class="fa fa-link mr-1"></i>
                    {{ $t("PRODUCTS.GO_TO_REPLACEMENT_LINE") }}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="alert alert-warning mt-3">
            <i class="fas fa-info-circle mr-2"></i>
            {{ $t("PRODUCTS.NO_REPLACEMENTS_AVAILABLE") }}
          </div>

          <div class="mt-3" v-if="selectedDeletedProduct.backorder_note">
            <strong>{{ $t("PRODUCTS.BACKORDER_NOTE") }}:</strong>
            <div class="mt-1">{{ selectedDeletedProduct.backorder_note }}</div>
          </div>
        </div>

        <div class="alert alert-warning mt-3">
          <i class="fas fa-info-circle mr-2"></i>
          {{ $t("PRODUCTS.DELETED_PRODUCT_QUANTITY_BLOCKED") }}
        </div>

        <div class="mt-4 text-right">
          <base-button
            type="secondary"
            @click="deletedProductModalOpened = false"
          >
            {{ $t("COMMON.CLOSE") }}
          </base-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import formMixin from "@/mixins/form-mixin";
import taxDisplayMixin from "@/mixins/tax-display-mixin";
import swal from "sweetalert2";
import { ORDER_STATUS_DRAFT } from "@/constants/orders";
import { STATUS_BACKORDER, STATUS_DELETED } from "@/constants/products";
import { BaseInput } from "@/components";
import { cloneDeep } from "lodash";
import defaultSalesOrderItem from "../defaultSalesOrderItem";
import CategoryGroupSelector from "@/components/CategoryGroupSelector.vue";
import SupplierSelector from "@/components/SupplierSelector.vue";
import TaxGroupsSelector from "@/components/TaxGroupsSelector.vue";
import ValidationError from "@/components/ValidationError.vue";
import {
  PRICING_TYPE_STANDARD,
  PRICING_TYPE_SUBSIDIZED,
  PRICING_TYPE_CATERER,
} from "@/constants/pricingTypes";

export default {
  name: "sales-order-view-details-sales-order-item-table-v2",

  components: {
    BaseInput,
    CategoryGroupSelector,
    SupplierSelector,
    TaxGroupsSelector,
    ValidationError,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [formMixin, taxDisplayMixin],

  props: ["salesOrder"],

  data() {
    const itemsQuantities = {};
    this.salesOrder.items.forEach((item) => {
      itemsQuantities[item.salesOrderable.id] = item.quantity;
    });

    const itemsByProduct = {};
    this.salesOrder.items.forEach((item) => {
      itemsByProduct[item.salesOrderable.id] = item;
    });

    return {
      pagination: {
        perPage: 500,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      loading: false,
      ORDER_STATUS_DRAFT: ORDER_STATUS_DRAFT,
      STATUS_BACKORDER: STATUS_BACKORDER,
      STATUS_DELETED: STATUS_DELETED,
      products: [],
      filteredProducts: [],
      itemsQuantities: itemsQuantities,
      itemsByProduct: itemsByProduct,
      query: "",
      displayZeroQuantities: this.salesOrder.status == ORDER_STATUS_DRAFT,
      selectedCategory: null,
      selectedSupplier: null,
      showDiscountModal: false,
      editingDiscountIndex: null,
      deletedProductModalOpened: false,
      selectedDeletedProduct: null,
      backorderModalOpened: false,
      selectedBackorderProduct: null,
      discountForm: {
        excerpt: "",
        discountType: "", // 'PERCENTAGE' or 'AMOUNT'
        discountValue: 0,
        taxGroups: [],
      },
      discountFormErrors: {},
      lastProductsFetchSignature: null,
      productsSignatureLoaded: null,
      activeProductsRequestToken: null,
    };
  },

  created() {
    this.getProducts();
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },

    total() {
      return this.filteredProducts.length;
    },

    canEditTaxGroups() {
      return (
        this.salesOrder.status === ORDER_STATUS_DRAFT &&
        this.$currentUserCan(this.$permissions.PERM_EDIT_ORDER_ITEM_TAX_GROUPS)
      );
    },

    startIndex() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage;
    },

    endIndex() {
      return this.startIndex + this.pagination.perPage;
    },

    isDiscountFormValid() {
      return (
        this.discountForm.excerpt?.trim() &&
        this.discountForm.discountType &&
        this.discountForm.discountValue > 0 &&
        (this.discountForm.discountType !== "PERCENTAGE" ||
          this.discountForm.discountValue <= 100)
      );
    },
  },

  methods: {
    async goToSalesOrderable(salesOrderable) {
      this.$router.push(this.$objectViewRoute(salesOrderable));
    },

    getItemTaxLabels(row) {
      return this.getOrderItemTaxLabels(row, this.itemsByProduct);
    },

    getItemTaxGroups(row) {
      const item = this.itemsByProduct?.[row.id];
      return item?.taxGroups || this.getProductTaxGroups(row);
    },

    showBackorderModal(product) {
      this.selectedBackorderProduct = product;
      this.backorderModalOpened = true;
    },

    showDeletedProductModal(product) {
      this.selectedDeletedProduct = product;
      this.deletedProductModalOpened = true;
    },

    getProductUnitPrice(product) {
      if (product.warehouseProduct) {
        if (this.salesOrder.pricing_type === PRICING_TYPE_SUBSIDIZED && product.warehouseProduct.subsidized_price > 0) {
          return product.warehouseProduct.subsidized_price;
        }
        if (this.salesOrder.pricing_type === PRICING_TYPE_CATERER && product.warehouseProduct.caterer_price > 0) {
          return product.warehouseProduct.caterer_price;
        }
        return product.warehouseProduct.selling_price;
      } else {
        return product.selling_price;
      }
    },

    /**
     * Container deposit carried by this product's order line, taken from the
     * line's pricing (the amount frozen when the line was created) rather than
     * from the product referential, which may have moved since.
     */
    getProductDepositAmount(product) {
      const item = this.itemsByProduct[product.id];

      return Number(item?.pricing?.deposit_amount ?? 0);
    },

    async getProducts({ force = false } = {}) {
      const signature = this.createProductsFetchSignature();
      if (!force && signature && this.productsSignatureLoaded === signature) {
        this.filterProducts();
        return;
      }

      if (
        !force &&
        this.loading &&
        signature &&
        this.lastProductsFetchSignature === signature
      ) {
        return;
      }

      const requestToken = Symbol("productsRequest");
      this.activeProductsRequestToken = requestToken;

      try {
        this.loading = true;
        this.lastProductsFetchSignature = signature;
        if (!this.salesOrder) {
          if (this.activeProductsRequestToken === requestToken) {
            this.loading = false;
            this.activeProductsRequestToken = null;
          }
          return;
        }

        let params = {
          sort: "-created_at",
          filter: {
            organization: this.salesOrder.organization?.id,
            can_be_sold: true,
            warehouse: this.salesOrder.sourceWarehouse?.id,
            ...(this.salesOrder.pricing_type === PRICING_TYPE_SUBSIDIZED && this.salesOrder.sourceWarehouse?.id
              ? { hasSubsidizedPrice: this.salesOrder.sourceWarehouse.id }
              : {}),
            ...(this.salesOrder.pricing_type === PRICING_TYPE_CATERER && this.salesOrder.sourceWarehouse?.id
              ? { hasCatererPrice: this.salesOrder.sourceWarehouse.id }
              : {}),
          },
          include:
            "taxGroups,unitOfMeasure,orderUnitOfMeasureUnit,categories,suppliers,replacements,unitOfMeasure.referenceUnit",
          page: {
            number: 1,
            size: 999999,
          },
        };

        await this.$store.dispatch("products/list", params);
        const productsArr = await this.$store.getters["products/list"];

        if (this.activeProductsRequestToken !== requestToken) {
          return;
        }

        this.products = productsArr;
        this.filterProducts();
        this.loading = false;
        this.lastProductsFetchSignature = signature;
        this.productsSignatureLoaded = signature;
        this.activeProductsRequestToken = null;
      } catch (error) {
        console.error(error);
        this.$notify({
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
          type: "error",
        });
        if (this.activeProductsRequestToken === requestToken) {
          this.loading = false;
          this.lastProductsFetchSignature = null;
          this.productsSignatureLoaded = null;
          this.activeProductsRequestToken = null;
        }
      }
    },

    createProductsFetchSignature() {
      if (!this.salesOrder) {
        return null;
      }

      const organizationId = this.salesOrder.organization?.id || "";
      const warehouseId = this.salesOrder.sourceWarehouse?.id || "";
      const pricingType = this.salesOrder.pricing_type || PRICING_TYPE_STANDARD;

      return `${organizationId}::${warehouseId}::${pricingType}`;
    },

    syncSalesOrderItems() {
      if (!this.salesOrder || !this.salesOrder.items) {
        return;
      }

      const itemsQuantities = {};
      const itemsByProduct = {};

      this.salesOrder.items.forEach((item) => {
        itemsQuantities[item.salesOrderable.id] = item.quantity;
        itemsByProduct[item.salesOrderable.id] = item;
      });

      this.itemsQuantities = itemsQuantities;
      this.itemsByProduct = itemsByProduct;
      this.filterProducts();
    },

    updateSalesOrderItem(product, quantity) {
      if (product.status === this.STATUS_BACKORDER) {
        this.resetSalesOrderProductQuantity(product);
        this.showBackorderModal(product);
        return;
      }

      if (product.status === this.STATUS_DELETED) {
        this.resetSalesOrderProductQuantity(product);
        this.showDeletedProductModal(product);
        return;
      }

      let salesOrderItemData = cloneDeep(defaultSalesOrderItem);

      const foundItem = this.salesOrder.items.find((item) => {
        return item.salesOrderable.id == product.id;
      });

      if (foundItem) {
        salesOrderItemData = cloneDeep(foundItem);
      }

      if (foundItem && quantity == 0) {
        this.$emit("onDeleteSalesOrderItem", foundItem.id);
        return;
      }

      salesOrderItemData.salesOrder.id = this.salesOrder.id;
      salesOrderItemData.salesOrderable = product;
      salesOrderItemData.excerpt = product.denomination;

      if (product.warehouseProduct) {
        if (this.salesOrder.pricing_type === PRICING_TYPE_SUBSIDIZED && product.warehouseProduct.subsidized_price > 0) {
          salesOrderItemData.unit_price = product.warehouseProduct.subsidized_price;
        } else if (this.salesOrder.pricing_type === PRICING_TYPE_CATERER && product.warehouseProduct.caterer_price > 0) {
          salesOrderItemData.unit_price = product.warehouseProduct.caterer_price;
        } else {
          salesOrderItemData.unit_price = product.warehouseProduct.selling_price;
        }
        salesOrderItemData.code =
          product.warehouseProduct.sku || product.sku || product.code;
      } else {
        salesOrderItemData.unit_price = product.selling_price;
        salesOrderItemData.code = product.code;
      }

      if (product.unitOfMeasure) {
        salesOrderItemData.unitOfMeasureUnit =
          product.unitOfMeasure.referenceUnit;
      }
      if (this.salesOrder.has_no_taxes) {
        salesOrderItemData.taxGroups = [];
      } else if (foundItem) {
        salesOrderItemData.taxGroups = foundItem.taxGroups || [];
      } else {
        salesOrderItemData.taxGroups = product.taxGroups;
      }

      salesOrderItemData.quantity = parseInt(quantity, 10);

      this.$emit("onSubmitSalesOrderItemForm", salesOrderItemData);
    },

    updateSalesOrderItemTaxGroups(product, taxGroups) {
      const foundItem = this.itemsByProduct?.[product.id];
      if (!foundItem) {
        return;
      }

      const salesOrderItemData = cloneDeep(foundItem);
      salesOrderItemData.salesOrder.id = this.salesOrder.id;
      salesOrderItemData.salesOrderable = product;
      salesOrderItemData.taxGroups = taxGroups;

      this.$emit("onSubmitSalesOrderItemForm", salesOrderItemData);
    },

    resetSalesOrderProductQuantity(product) {
      const foundItem = this.salesOrder.items.find((item) => {
        return item.salesOrderable.id == product.id;
      });
      if (foundItem) {
        this.itemsQuantities[product.id] = foundItem.quantity;
      } else {
        this.itemsQuantities[product.id] = 0;
      }
    },

    async copyReplacementCode(code) {
      if (!code) {
        return;
      }

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = code;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "absolute";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.$notify({
          type: "success",
          message: this.$t("PRODUCTS.REPLACEMENT_CODE_COPIED"),
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    scrollToReplacement(productId) {
      if (!productId) {
        return;
      }

      const quantity = this.itemsQuantities[productId] || 0;
      if (!this.displayZeroQuantities && quantity <= 0) {
        this.displayZeroQuantities = true;
        this.$nextTick(() => {
          this.scrollToReplacement(productId);
        });
        return;
      }

      this.$nextTick(() => {
        const productIndex = this.filteredProducts.findIndex(
          (product) => product.id === productId
        );

        if (productIndex === -1) {
          this.$notify({
            type: "warning",
            message: this.$t("PRODUCTS.REPLACEMENT_LINE_NOT_FOUND"),
          });
          return;
        }

        const targetPage =
          Math.floor(productIndex / this.pagination.perPage) + 1;
        if (this.pagination.currentPage !== targetPage) {
          this.pagination.currentPage = targetPage;
          this.$nextTick(() => {
            this.scrollToReplacement(productId);
          });
          return;
        }

        const tableEl =
          this.$refs.salesOrderItemsTable &&
          this.$refs.salesOrderItemsTable.$el;
        const rowEl =
          tableEl && tableEl.querySelector(`.product-row-${productId}`);

        if (rowEl) {
          rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
          rowEl.classList.add("replacement-target-highlight");
          setTimeout(() => {
            rowEl.classList.remove("replacement-target-highlight");
          }, 10000);
          this.backorderModalOpened = false;
          this.deletedProductModalOpened = false;
        } else {
          this.$notify({
            type: "warning",
            message: this.$t("PRODUCTS.REPLACEMENT_LINE_NOT_FOUND"),
          });
        }
      });
    },

    getProductRowClassName(rowData) {
      const row = rowData && rowData.row ? rowData.row : rowData;
      return row && row.id ? `product-row-${row.id}` : "";
    },

    filterProducts() {
      const query = this.query;
      const augmentedProducts = [...(this.products || [])];
      const productIds = new Set(
        augmentedProducts
          .filter((product) => product && product.id)
          .map((product) => product.id)
      );

      Object.values(this.itemsByProduct || {}).forEach((item) => {
        const salesOrderable = item?.salesOrderable;

        if (
          !salesOrderable ||
          !salesOrderable.id ||
          productIds.has(salesOrderable.id)
        ) {
          return;
        }

        const fallbackProduct = {
          ...salesOrderable,
          categories:
            salesOrderable.categories || item.categories || [],
          suppliers: salesOrderable.suppliers || item.suppliers || [],
          taxGroups: salesOrderable.taxGroups || item.taxGroups || [],
          unitOfMeasure:
            salesOrderable.unitOfMeasure || item.unitOfMeasure || null,
          warehouseProduct:
            salesOrderable.warehouseProduct || item.warehouseProduct || null,
          replacements:
            salesOrderable.replacements || item.replacements || [],
        };

        augmentedProducts.push(fallbackProduct);
        productIds.add(fallbackProduct.id);
      });

      const filteredData = augmentedProducts.filter((product) => {
        const matchesQuery =
          !this.query ||
          this.$unaccent(product.code || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.sku || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.name || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.supplierProduct?.code || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.supplierProduct?.sku || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase());

        const quantity = this.itemsQuantities[product.id] || 0;
        const matchesQuantityFilter = this.displayZeroQuantities
          ? true
          : quantity > 0;

        const matchCategory =
          !this.selectedCategory ||
          product.categories?.some(
            (category) => category.id === this.selectedCategory
          );

        const matchSupplier =
          !this.selectedSupplier ||
          product.suppliers?.some(
            (supplier) => supplier.id === this.selectedSupplier
          );

        return (
          matchesQuery &&
          matchesQuantityFilter &&
          matchCategory &&
          matchSupplier
        );
      });

      this.filteredProducts = filteredData;
    },

    openDiscountModal() {
      this.showDiscountModal = true;
      this.editingDiscountIndex = null;
      this.discountForm = {
        excerpt: this.$t("SALES_ORDERS.DISCOUNT"),
        discountType: "",
        discountValue: 0,
        taxGroups: [],
      };
      this.discountFormErrors = {};
    },

    editDiscount(index) {
      this.showDiscountModal = true;
      this.editingDiscountIndex = index;
      const discount = this.salesOrder.discounts[index];

      this.discountForm = {
        excerpt: discount.name || "",
        discountType: discount.type || "",
        discountValue: discount.value || 0,
        taxGroups: discount.taxGroups || [],
      };
      this.discountFormErrors = {};
    },

    async deleteDiscount(index) {
      const result = await swal.fire({
        title: this.$t("SALES_ORDERS.CONFIRM_DELETE_DISCOUNT"),
        text: this.$t("COMMON.ACTION_CANNOT_BE_UNDONE"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("COMMON.DELETE"),
        cancelButtonText: this.$t("COMMON.CANCEL"),
      });

      if (result.isConfirmed) {
        const salesOrderData = cloneDeep(this.salesOrder);
        salesOrderData.discounts = salesOrderData.discounts.filter(
          (_, i) => i !== index
        );

        try {
          await this.$store.dispatch("salesOrders/update", salesOrderData);
          this.$notify({
            type: "success",
            message: this.$t("SALES_ORDERS.DISCOUNT_DELETED"),
          });
          this.$emit("onSalesOrderUpdated", null);
        } catch (error) {
          console.log(error);
          this.$notify({
            type: "error",
            message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
          });
        }
      }
    },

    closeDiscountModal() {
      this.showDiscountModal = false;
      this.editingDiscountIndex = null;
      this.discountForm = {
        excerpt: "",
        discountType: "",
        discountValue: 0,
        taxGroups: [],
      };
      this.discountFormErrors = {};
    },

    onDiscountTypeChange() {
      this.discountForm.discountValue = 0;
    },

    updateTaxGroups(taxGroupIds) {
      // Get full tax group objects from the store or API
      this.discountForm.taxGroups = taxGroupIds.map((id) => ({
        id,
        type: "tax-groups",
      }));
    },

    calculateDiscountAmount() {
      if (this.discountForm.discountType === "PERCENTAGE") {
        const subtotal = this.salesOrder.pricing?.subtotal || 0;
        return (subtotal * this.discountForm.discountValue) / 100;
      }
      return this.discountForm.discountValue;
    },

    calculateDiscountAmountFromDiscount(discount) {
      if (discount.type === "PERCENTAGE") {
        const subtotal = this.salesOrder.pricing?.subtotal || 0;
        return (subtotal * discount.value) / 100;
      }
      return discount.value;
    },

    validateDiscountForm() {
      this.discountFormErrors = {};

      if (!this.discountForm.excerpt?.trim()) {
        this.discountFormErrors.excerpt = [
          this.$t("VALIDATION.REQUIRED_FIELD"),
        ];
      }

      if (!this.discountForm.discountType) {
        this.discountFormErrors.discountType = [
          this.$t("VALIDATION.REQUIRED_FIELD"),
        ];
      }

      if (
        !this.discountForm.discountValue ||
        this.discountForm.discountValue <= 0
      ) {
        this.discountFormErrors.discountValue = [
          this.$t("VALIDATION.REQUIRED_POSITIVE_NUMBER"),
        ];
      }

      if (
        this.discountForm.discountType === "PERCENTAGE" &&
        this.discountForm.discountValue > 100
      ) {
        this.discountFormErrors.discountValue = [
          this.$t("VALIDATION.PERCENTAGE_MAX_100"),
        ];
      }

      return Object.keys(this.discountFormErrors).length === 0;
    },

    async submitDiscount() {
      if (!this.validateDiscountForm()) {
        return;
      }

      const salesOrderData = cloneDeep(this.salesOrder);
      const discountData = {
        type: this.discountForm.discountType,
        name: this.discountForm.excerpt,
        value: this.discountForm.discountValue,
        taxGroups: this.discountForm.taxGroups,
      };

      if (this.editingDiscountIndex !== null) {
        // Update existing discount
        salesOrderData.discounts[this.editingDiscountIndex] = discountData;
      } else {
        // Add new discount
        salesOrderData.discounts = [
          ...(salesOrderData.discounts || []),
          discountData,
        ];
      }

      try {
        await this.$store.dispatch("salesOrders/update", salesOrderData);
        this.$notify({
          message:
            this.editingDiscountIndex !== null
              ? this.$t("SALES_ORDERS.DISCOUNT_UPDATED")
              : this.$t("SALES_ORDERS.DISCOUNT_ADDED"),
          type: "success",
        });
        this.$emit("onSalesOrderUpdated", null);
      } catch (error) {
        console.log(error);
        this.$notify({
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
          type: "error",
        });
        return; // Don't close modal on error
      }
      this.closeDiscountModal();
    },
  },

  watch: {
    salesOrder: {
      handler() {
        this.getProducts();
        this.syncSalesOrderItems();

        this.displayZeroQuantities =
          this.salesOrder.status == ORDER_STATUS_DRAFT;
      },
      deep: true,
    },

    query: {
      handler() {
        this.filterProducts();
      },
      immediate: true,
      deep: true,
    },

    displayZeroQuantities: {
      handler() {
        this.filterProducts();
      },
      immediate: true,
      deep: true,
    },

    pagination: {
      handler() {
        this.filterProducts();
      },
      immediate: false,
      deep: true,
    },

    selectedCategory: {
      handler() {
        this.filterProducts();
      },
      immediate: false,
      deep: true,
    },

    selectedSupplier: {
      handler() {
        this.filterProducts();
      },
      immediate: false,
      deep: true,
    },
  },
};
</script>

<style>
.salesOrder-item-excerpt p,
.salesOrder-item-excerpt,
.salesOrder-item-excerpt * {
  font-size: 0.7rem;
}

.discount-list {
  width: 100%;
}

.discount-item {
  width: 100%;
}

.card-stats {
  border: 1px solid #e3e3e3;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
  width: 100%;
}

.card-stats:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.card-stats .card-body {
  padding: 1rem;
  width: 100%;
}

.card-stats .card-title {
  color: #333;
  font-weight: 600;
}

.card-stats .card-text {
  color: #666;
  font-size: 0.875rem;
}

.discount-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.flex-grow-1 {
  flex-grow: 1;
}

.replacement-actions .action-icon {
  cursor: pointer;
}

.replacement-link {
  font-size: 0.85rem;
}

.replacement-meta {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2d3d;
}

.replacement-target-highlight {
  animation: replacement-target-pulse 10s ease;
}

.replacement-target-highlight > td {
  background-color: #f8d7da !important;
  transition: background-color 0.3s ease;
}

@keyframes replacement-target-pulse {
  0% {
    background-color: #f8d7da;
  }
  80% {
    background-color: #f8d7da;
  }
  100% {
    background-color: transparent;
  }
}
</style>
